import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { EmptyState, LoadingState, MetricCard, PageHeader } from './AdminUI';
import { API_BASE } from '../../config/api';
import { BRAND_BLUE } from '../../theme';
import { alertError, alertSuccess, confirmDelete } from '../../utils/swal';

const ServicesManagement = () => {
    const imageInputRef = useRef(null);
    const { apiCall } = useAuth();
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        description: '',
        icon: 'fa-bar-chart',
        iconColor: BRAND_BLUE,
        image: '',
        overview: '',
        keyFeatures: [],
        methodologies: [],
        deliverables: [],
        industries: [],
        order: 0,
        active: true
    });
    const [featureInput, setFeatureInput] = useState({ title: '', description: '' });
    const [methodologyInput, setMethodologyInput] = useState('');
    const [deliverableInput, setDeliverableInput] = useState('');
    const [industryInput, setIndustryInput] = useState('');

    useEffect(() => {
        fetchServices();
    }, []);

    const fetchServices = async () => {
        try {
            const response = await apiCall('/services');
            const data = await response.json();
            // Ensure we always set an array
            const services = Array.isArray(data.services) ? data.services :
                           Array.isArray(data) ? data : [];
            setServices(services);
        } catch (error) {
            console.error('Error fetching services:', error);
            setServices([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingService ? `/services/${editingService.id}` : '/services';
            const method = editingService ? 'PUT' : 'POST';

            let body;
            if (imageFile) {
                const fd = new FormData();
                fd.append('title', formData.title);
                fd.append('slug', formData.slug);
                fd.append('description', formData.description);
                fd.append('icon', formData.icon);
                fd.append('iconColor', formData.iconColor);
                fd.append('overview', formData.overview);
                fd.append('order', String(formData.order));
                fd.append('active', String(formData.active));
                fd.append('keyFeatures', JSON.stringify(formData.keyFeatures));
                fd.append('methodologies', JSON.stringify(formData.methodologies));
                fd.append('deliverables', JSON.stringify(formData.deliverables));
                fd.append('industries', JSON.stringify(formData.industries));
                fd.append('image', imageFile);
                body = fd;
            } else {
                body = JSON.stringify({ ...formData, image: formData.image || null });
            }

            const response = await apiCall(url, { method, body });

            if (response.ok) {
                await fetchServices();
                handleCloseModal();
                alertSuccess('Saved', 'Service saved successfully.');
            } else {
                const result = await response.json();
                alertError('Error', result.message || 'Error saving service');
            }
        } catch (error) {
            console.error('Error saving service:', error);
            alertError('Error', 'Error saving service');
        }
    };

    const handleEdit = (service) => {
        setEditingService(service);
        setImageFile(null);
        setFormData({
            title: service.title,
            slug: service.slug,
            description: service.description,
            icon: service.icon,
            iconColor: service.iconColor,
            image: service.image || '',
            overview: service.overview,
            keyFeatures: Array.isArray(service.keyFeatures) ? service.keyFeatures : [],
            methodologies: Array.isArray(service.methodologies) ? service.methodologies : [],
            deliverables: Array.isArray(service.deliverables) ? service.deliverables : [],
            industries: Array.isArray(service.industries) ? service.industries : [],
            order: service.order,
            active: service.active
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        const { isConfirmed } = await confirmDelete({ title: 'Delete service?', text: 'Are you sure you want to delete this service? This cannot be undone.' });
        if (!isConfirmed) return;

        try {
            const response = await apiCall(`/services/${id}`, { method: 'DELETE' });
            if (response.ok) {
                await fetchServices();
                alertSuccess('Deleted', 'Service deleted successfully.');
            } else {
                alertError('Error', 'Error deleting service');
            }
        } catch (error) {
            console.error('Error deleting service:', error);
            alertError('Error', 'Error deleting service');
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingService(null);
        setImageFile(null);
        setFormData({
            title: '',
            slug: '',
            description: '',
            icon: 'fa-bar-chart',
            iconColor: BRAND_BLUE,
            image: '',
            overview: '',
            keyFeatures: [],
            methodologies: [],
            deliverables: [],
            industries: [],
            order: 0,
            active: true
        });
        setFeatureInput({ title: '', description: '' });
        setMethodologyInput('');
        setDeliverableInput('');
        setIndustryInput('');
    };

    const imagePreviewUrl = imageFile
        ? URL.createObjectURL(imageFile)
        : (formData.image && formData.image.startsWith('uploads/')
            ? `${API_BASE.replace(/\/api\/?$/, '')}/${formData.image}`
            : null);

    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();
    };

    const handleTitleChange = (e) => {
        const title = e.target.value;
        setFormData({ ...formData, title, slug: generateSlug(title) });
    };

    const handleAddKeyFeature = () => {
        if (featureInput.title.trim() && featureInput.description.trim()) {
            setFormData({
                ...formData,
                keyFeatures: [...formData.keyFeatures, { ...featureInput }]
            });
            setFeatureInput({ title: '', description: '' });
        }
    };

    const handleRemoveKeyFeature = (index) => {
        setFormData({
            ...formData,
            keyFeatures: formData.keyFeatures.filter((_, i) => i !== index)
        });
    };

    const handleAddMethodology = () => {
        if (methodologyInput.trim() && !formData.methodologies.includes(methodologyInput.trim())) {
            setFormData({ ...formData, methodologies: [...formData.methodologies, methodologyInput.trim()] });
            setMethodologyInput('');
        }
    };

    const handleAddDeliverable = () => {
        if (deliverableInput.trim() && !formData.deliverables.includes(deliverableInput.trim())) {
            setFormData({ ...formData, deliverables: [...formData.deliverables, deliverableInput.trim()] });
            setDeliverableInput('');
        }
    };

    const handleAddIndustry = () => {
        if (industryInput.trim() && !formData.industries.includes(industryInput.trim())) {
            setFormData({ ...formData, industries: [...formData.industries, industryInput.trim()] });
            setIndustryInput('');
        }
    };

    const iconOptions = [
        'fa-chart-bar', 'fa-graduation-cap', 'fa-users', 'fa-briefcase',
        'fa-file-text', 'fa-database', 'fa-line-chart', 'fa-pie-chart',
        'fa-cogs', 'fa-laptop', 'fa-book', 'fa-lightbulb-o'
    ];

    const serviceStats = useMemo(() => ({
        total: services.length,
        active: services.filter((service) => service.active).length,
        inactive: services.filter((service) => !service.active).length,
        features: services.reduce((count, service) => count + (Array.isArray(service.keyFeatures) ? service.keyFeatures.length : 0), 0)
    }), [services]);

    if (loading) {
        return (
            <AdminLayout>
                <LoadingState label="Loading services..." />
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="admin-page">
                <PageHeader
                    title="Services"
                    subtitle="Manage your service offerings"
                    action={
                    <button
                        onClick={() => setShowModal(true)}
                        className="btn btn-primary"
                    >
                        <i className="fa fa-plus"></i>
                        Add Service
                    </button>
                    }
                />

                <div className="analytics-metrics-grid">
                    <MetricCard label="Total Services" value={serviceStats.total} icon="fa fa-cogs" accent="#62B6E8" detail="All offerings" />
                    <MetricCard label="Active" value={serviceStats.active} icon="fa fa-eye" accent="#62B6E8" detail="Visible on website" />
                    <MetricCard label="Inactive" value={serviceStats.inactive} icon="fa fa-eye-slash" accent="#62B6E8" detail="Hidden services" />
                    <MetricCard label="Features" value={serviceStats.features} icon="fa fa-list-ul" accent="#62B6E8" detail="Published feature points" />
                </div>

                <div className="admin-card-grid is-wide">
                    {services.length === 0 ? (
                        <div style={{ gridColumn: '1/-1' }}>
                            <EmptyState
                                icon="fa fa-cogs"
                                title="No services yet"
                                text="Add your first service to publish an offering."
                            />
                        </div>
                    ) : (
                        services.map((service) => (
                            <div key={service.id} className="service-admin-card">
                                <div className="service-card-accent" style={{ background: service.iconColor || BRAND_BLUE }}></div>
                                <div className="service-card-head">
                                    <div className="service-card-icon" style={{ color: service.iconColor || BRAND_BLUE }}>
                                        <i className={`fa ${service.icon}`}></i>
                                    </div>
                                    <div className="service-card-title-block">
                                        <h3>{service.title}</h3>
                                        <span className={`admin-pill ${service.active ? 'admin-pill-success' : 'admin-pill-muted'}`}>
                                            {service.active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                </div>
                                <p className="service-card-description">
                                    {service.description}
                                </p>
                                <div className="service-card-meta">
                                    <span><i className="fa fa-list-ul"></i>{Array.isArray(service.keyFeatures) ? service.keyFeatures.length : 0} features</span>
                                    <span><i className="fa fa-sort"></i>Order {service.order ?? 0}</span>
                                </div>
                                <div className="service-card-actions">
                                    <button
                                        onClick={() => handleEdit(service)}
                                        className="btn btn-primary"
                                    >
                                        <i className="fa fa-edit"></i> Edit
                                    </button>
                                    <button
                                        onClick={() => handleDelete(service.id)}
                                        className="btn btn-danger btn-icon"
                                    >
                                        <i className="fa fa-trash"></i>
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Modal */}
                {showModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000
                    }}>
                        <div style={{
                            background: 'white',
                            borderRadius: '0',
                            maxWidth: '800px',
                            width: '90%',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            padding: '32px'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
                                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1a202c' }}>
                                    {editingService ? 'Edit Service' : 'Add Service'}
                                </h2>
                                <button
                                    onClick={handleCloseModal}
                                    style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#718096' }}
                                >
                                    <i className="fa fa-times"></i>
                                </button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Service Title *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={handleTitleChange}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '0',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Slug *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.slug}
                                        onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '0',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Short Description *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '0',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Service Image
                                    </label>
                                    <input
                                        ref={imageInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/gif,image/webp"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            setImageFile(file || null);
                                        }}
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '0',
                                            fontSize: '14px'
                                        }}
                                    />
                                    {imagePreviewUrl && (
                                        <div style={{ marginTop: '12px' }}>
                                            <img
                                                src={imagePreviewUrl}
                                                alt="Preview"
                                                style={{
                                                    maxWidth: '200px',
                                                    maxHeight: '160px',
                                                    objectFit: 'cover',
                                                    border: '1px solid #e2e8f0'
                                                }}
                                            />
                                            {imageFile && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setImageFile(null);
                                                        if (imageInputRef.current) imageInputRef.current.value = '';
                                                    }}
                                                    style={{
                                                        display: 'block',
                                                        marginTop: '8px',
                                                        padding: '4px 12px',
                                                        background: '#e2e8f0',
                                                        border: 'none',
                                                        borderRadius: '0',
                                                        cursor: 'pointer',
                                                        fontSize: '13px'
                                                    }}
                                                >
                                                    Remove image
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                            Icon
                                        </label>
                                        <select
                                            value={formData.icon}
                                            onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px'
                                            }}
                                        >
                                            {iconOptions.map(icon => (
                                                <option key={icon} value={icon}>{icon}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                            Icon Color
                                        </label>
                                        <input
                                            type="color"
                                            value={formData.iconColor}
                                            onChange={(e) => setFormData({ ...formData, iconColor: e.target.value })}
                                            style={{
                                                width: '100%',
                                                height: '42px',
                                                padding: '4px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                            Order
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.order}
                                            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px'
                                            }}
                                        />
                                    </div>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Overview *
                                    </label>
                                    <textarea
                                        value={formData.overview}
                                        onChange={(e) => setFormData({ ...formData, overview: e.target.value })}
                                        required
                                        rows={4}
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '2px solid #e2e8f0',
                                            borderRadius: '0',
                                            fontSize: '14px',
                                            resize: 'vertical'
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                        Key Features
                                    </label>
                                    <div style={{ background: '#f7fafc', padding: '16px', borderRadius: '0', marginBottom: '8px' }}>
                                        <input
                                            type="text"
                                            placeholder="Feature title"
                                            value={featureInput.title}
                                            onChange={(e) => setFeatureInput({ ...featureInput, title: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px',
                                                marginBottom: '8px'
                                            }}
                                        />
                                        <input
                                            type="text"
                                            placeholder="Feature description"
                                            value={featureInput.description}
                                            onChange={(e) => setFeatureInput({ ...featureInput, description: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '0',
                                                fontSize: '14px',
                                                marginBottom: '8px'
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddKeyFeature}
                                            style={{
                                                padding: '8px 16px',
                                                background: '#f59e0b',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '0',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Add Feature
                                        </button>
                                    </div>
                                    <div>
                                        {formData.keyFeatures.map((feature, index) => (
                                            <div key={index} style={{
                                                background: '#fef3c7',
                                                padding: '12px',
                                                borderRadius: '0',
                                                marginBottom: '8px',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start'
                                            }}>
                                                <div>
                                                    <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '4px' }}>
                                                        {feature.title}
                                                    </div>
                                                    <div style={{ fontSize: '13px', color: '#78350f' }}>
                                                        {feature.description}
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveKeyFeature(index)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400e' }}
                                                >
                                                    <i className="fa fa-times"></i>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                            Methodologies
                                        </label>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <input
                                                type="text"
                                                value={methodologyInput}
                                                onChange={(e) => setMethodologyInput(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddMethodology())}
                                                style={{
                                                    flex: 1,
                                                    padding: '8px 10px',
                                                    border: '2px solid #e2e8f0',
                                                    borderRadius: '0',
                                                    fontSize: '13px'
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleAddMethodology}
                                                style={{
                                                    padding: '8px 12px',
                                                    background: BRAND_BLUE,
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '0',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <i className="fa fa-plus"></i>
                                            </button>
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                                            {formData.methodologies.map((item, i) => (
                                                <span key={i} style={{
                                                    background: '#e3f2fd',
                                                    color: '#1976d2',
                                                    padding: '4px 10px',
                                                    borderRadius: '10px',
                                                    fontSize: '11px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}>
                                                    {item}
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData({ ...formData, methodologies: formData.methodologies.filter((_, idx) => idx !== i) })}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1976d2', fontSize: '10px' }}
                                                    >
                                                        <i className="fa fa-times"></i>
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                            Deliverables
                                        </label>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <input
                                                type="text"
                                                value={deliverableInput}
                                                onChange={(e) => setDeliverableInput(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddDeliverable())}
                                                style={{
                                                    flex: 1,
                                                    padding: '8px 10px',
                                                    border: '2px solid #e2e8f0',
                                                    borderRadius: '0',
                                                    fontSize: '13px'
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleAddDeliverable}
                                                style={{
                                                    padding: '8px 12px',
                                                    background: BRAND_BLUE,
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '0',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <i className="fa fa-plus"></i>
                                            </button>
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                                            {formData.deliverables.map((item, i) => (
                                                <span key={i} style={{
                                                    background: '#d1fae5',
                                                    color: '#065f46',
                                                    padding: '4px 10px',
                                                    borderRadius: '10px',
                                                    fontSize: '11px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}>
                                                    {item}
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData({ ...formData, deliverables: formData.deliverables.filter((_, idx) => idx !== i) })}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#065f46', fontSize: '10px' }}
                                                    >
                                                        <i className="fa fa-times"></i>
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <label style={{ display: 'block', fontWeight: '500', color: '#2d3748', marginBottom: '6px' }}>
                                            Industries
                                        </label>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <input
                                                type="text"
                                                value={industryInput}
                                                onChange={(e) => setIndustryInput(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddIndustry())}
                                                style={{
                                                    flex: 1,
                                                    padding: '8px 10px',
                                                    border: '2px solid #e2e8f0',
                                                    borderRadius: '0',
                                                    fontSize: '13px'
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleAddIndustry}
                                                style={{
                                                    padding: '8px 12px',
                                                    background: BRAND_BLUE,
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '0',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <i className="fa fa-plus"></i>
                                            </button>
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                                            {formData.industries.map((item, i) => (
                                                <span key={i} style={{
                                                    background: '#f3e8ff',
                                                    color: '#7c3aed',
                                                    padding: '4px 10px',
                                                    borderRadius: '10px',
                                                    fontSize: '11px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}>
                                                    {item}
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData({ ...formData, industries: formData.industries.filter((_, idx) => idx !== i) })}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7c3aed', fontSize: '10px' }}
                                                    >
                                                        <i className="fa fa-times"></i>
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ marginBottom: '24px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={formData.active}
                                            onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                            style={{ width: '18px', height: '18px' }}
                                        />
                                        <span style={{ fontWeight: '500', color: '#2d3748' }}>Active (visible on website)</span>
                                    </label>
                                </div>

                                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        style={{
                                            padding: '12px 24px',
                                            background: '#e2e8f0',
                                            color: '#4a5568',
                                            border: 'none',
                                            borderRadius: '0',
                                            cursor: 'pointer',
                                            fontWeight: '500'
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        style={{
                                            padding: '12px 24px',
                                            background: '#f59e0b',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '0',
                                            cursor: 'pointer',
                                            fontWeight: '500'
                                        }}
                                    >
                                        {editingService ? 'Update' : 'Add'} Service
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default ServicesManagement;
